import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, IconButton, Message, Modal, Panel, SelectPicker, Stack, Tooltip, Whisper, toaster } from 'rsuite';
import FileExcelO from '@rsuite/icons/legacy/FileExcelO';

import ProviderBadge from '../components/ProviderBadge';

import './ActualsPrint.css';
import { exportSheetsToXlsx } from '../utils/excelExport';

import {
  useTaskOrderTaskMonthRollup,
  useTaskOrderActualsForMonths,
  useTaskOrderHourForecastForMonths,
  useProjects,
  useActiveProvider,
  useTaskOrders,
  useUsers,
  useCloseDates,
} from '../hooks/useQueries';
import { formatHours } from '../utils/chartUtils';

import useAuthenticatedFetch from '../hooks/useAuthenticatedFetch';
import API_CONFIG from '../config/apiConfig';

const isIsoDate = (s) => typeof s === 'string' && /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(s);

const formatMonthLabel = (monthStart) => {
  if (!isIsoDate(monthStart)) return '—';
  const [y, m] = monthStart.split('-').map((v) => Number(v));
  if (!Number.isInteger(y) || !Number.isInteger(m)) return '—';
  const d = new Date(y, m - 1, 1);
  return d.toLocaleString(undefined, { month: 'short', year: '2-digit' });
};

const toMonthStartIso = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const getCurrentYearStartMonthIso = () => {
  const now = new Date();
  return toMonthStartIso(new Date(now.getFullYear(), 0, 1));
};

const addMonthsIso = (monthStart, deltaMonths) => {
  if (!isIsoDate(monthStart)) return null;
  const y = Number(String(monthStart).slice(0, 4));
  const m0 = Number(String(monthStart).slice(5, 7)) - 1;
  if (!Number.isInteger(y) || !Number.isInteger(m0)) return null;
  const d = new Date(y, m0 + Number(deltaMonths || 0), 1);
  d.setHours(0, 0, 0, 0);
  return toMonthStartIso(d);
};

const resolveApiBaseUrl = () => {
  const raw = String(API_CONFIG?.BASE_URL || '').trim();
  if (!raw) return '/api';

  const withoutTrailing = raw.replace(/\/+$/, '');
  if (/\/api$/i.test(withoutTrailing)) return withoutTrailing;
  return `${withoutTrailing}/api`;
};

const roundToQuarterHour = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n / 0.25) * 0.25;
};

const formatQuarterHours = (value) => {
  const r = roundToQuarterHour(value);
  return r.toFixed(2);
};

const formatDeltaHours = (delta) => {
  const n = Number(delta);
  if (!Number.isFinite(n)) return '';
  const r = roundToQuarterHour(n);
  if (Math.abs(r) < 0.01) return '0.00';
  const s = formatQuarterHours(r);
  return r > 0 ? `+${s}` : s;
};

const normalizeIsoTimestamp = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const latestIsoTimestamp = (a, b) => {
  const aa = normalizeIsoTimestamp(a);
  const bb = normalizeIsoTimestamp(b);
  if (aa && bb) return aa >= bb ? aa : bb;
  return aa || bb || null;
};

const formatDaysAgoLabel = (value) => {
  const iso = normalizeIsoTimestamp(value);
  if (!iso) return null;

  const then = new Date(iso);
  const now = new Date();
  const thenDay = new Date(then.getFullYear(), then.getMonth(), then.getDate());
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffMs = nowDay.getTime() - thenDay.getTime();
  const days = Number.isFinite(diffMs) ? Math.max(0, Math.floor(diffMs / (24 * 60 * 60 * 1000))) : null;
  if (!Number.isInteger(days)) return null;
  if (days === 0) return 'today';

  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const formatRefreshedLabel = (value) => {
  const iso = normalizeIsoTimestamp(value);
  if (!iso) return null;
  const dayLabel = formatDaysAgoLabel(iso);
  if (!dayLabel) return null;
  if (dayLabel === 'today') {
    const t = new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `today at ${t}`;
  }
  return dayLabel;
};

const countWeekdaysInRange = (startInclusive, endExclusive) => {
  if (!(startInclusive instanceof Date) || !(endExclusive instanceof Date)) return 0;

  const start = new Date(startInclusive.getFullYear(), startInclusive.getMonth(), startInclusive.getDate());
  const end = new Date(endExclusive.getFullYear(), endExclusive.getMonth(), endExclusive.getDate());
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  if (start.getTime() >= end.getTime()) return 0;

  let count = 0;
  const cursor = new Date(start);
  while (cursor.getTime() < end.getTime()) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

const parseMonthDay = (raw) => {
  const s = String(raw ?? '').slice(0, 10);
  const m = s.match(/^\d{4}-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return { month: Number(m[1]), day: Number(m[2]) };
};

const countCloseDatesInRange = (startInclusive, endExclusive, rows) => {
  if (!(startInclusive instanceof Date) || !(endExclusive instanceof Date)) return 0;
  const list = Array.isArray(rows) ? rows : [];

  const start = new Date(startInclusive.getFullYear(), startInclusive.getMonth(), startInclusive.getDate());
  const end = new Date(endExclusive.getFullYear(), endExclusive.getMonth(), endExclusive.getDate());
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  if (start.getTime() >= end.getTime()) return 0;

  const years = new Set();
  for (let y = start.getFullYear(); y <= end.getFullYear(); y += 1) years.add(y);

  let count = 0;
  for (const r of list) {
    const md = parseMonthDay(r?.closeDate ?? r?.close_date);
    if (!md || !Number.isFinite(md.month) || !Number.isFinite(md.day)) continue;

    for (const y of years) {
      const d = new Date(y, md.month - 1, md.day);
      d.setHours(0, 0, 0, 0);
      if (d.getTime() >= start.getTime() && d.getTime() < end.getTime()) {
        count += 1;
      }
    }
  }

  return count;
};

const shouldWarnProjectedShortfall = ({ monthStart, projected, actual, currentMonthStart, ftePercent, closeDates }) => {
  if (String(monthStart) !== String(currentMonthStart)) return false;

  const proj = Number(projected);
  const act = Number(actual);
  if (!Number.isFinite(proj) || proj <= 0) return false;
  if (!Number.isFinite(act)) return false;

  const minTarget = proj * 0.95;
  if (act >= minTarget) return false;

  const y = Number(String(monthStart).slice(0, 4));
  const m0 = Number(String(monthStart).slice(5, 7)) - 1;
  if (!Number.isInteger(y) || !Number.isInteger(m0)) return false;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  today.setHours(0, 0, 0, 0);

  const monthStartDate = new Date(y, m0, 1);
  monthStartDate.setHours(0, 0, 0, 0);
  const monthEndExclusive = new Date(y, m0 + 1, 1);
  monthEndExclusive.setHours(0, 0, 0, 0);

  const rangeStart = today.getTime() > monthStartDate.getTime() ? today : monthStartDate;
  if (rangeStart.getTime() >= monthEndExclusive.getTime()) return false;

  const weekdaysRemaining = countWeekdaysInRange(rangeStart, monthEndExclusive);
  const closeDaysRemaining = countCloseDatesInRange(rangeStart, monthEndExclusive, closeDates);

  const rawFte = Number(ftePercent);
  const fteScale = Number.isFinite(rawFte) ? Math.max(0, rawFte) / 100 : 1;
  const remainingCapacity = Math.max(0, weekdaysRemaining * 8 - closeDaysRemaining * 8) * fteScale;

  const maxReachable = act + remainingCapacity;
  return maxReachable < minTarget;
};

const DeltaBadge = ({ delta, projected, actual, shortfallWarning }) => {
  const n = Number(delta);
  if (!Number.isFinite(n)) return null;

  const proj = Number(projected);
  const act = Number(actual);

  const isProjZero = !Number.isFinite(proj) || Math.abs(proj) < 0.01;
  const isActZero = !Number.isFinite(act) || Math.abs(act) < 0.01;

  let bg = '#fffbe6';
  let border = '#ffe58f';
  let color = '#ad8b00';
  let showOverageWarning = false;

  if (isProjZero && isActZero) {
    bg = '#f6ffed';
    border = '#b7eb8f';
    color = '#389e0d';
  } else if (isProjZero && !isActZero) {
    bg = '#fff1f0';
    border = '#ffa39e';
    color = '#cf1322';
    showOverageWarning = true;
  } else {
    const pct = ((act - proj) / Math.abs(proj)) * 100;
    const absPct = Math.abs(pct);

    if (absPct <= 5) {
      bg = '#f6ffed';
      border = '#b7eb8f';
      color = '#389e0d';
    } else if (pct > 10) {
      bg = '#fff1f0';
      border = '#ffa39e';
      color = '#cf1322';
      showOverageWarning = true;
    }
  }

  const warningMessage = showOverageWarning
    ? 'Actual hours are > 110% of the projected hours.'
    : shortfallWarning
      ? 'User will likely not be able to meet the projected hours based on available time in the month.'
      : null;
  const warningColor = showOverageWarning ? '#cf1322' : '#fa8c16';
  const badgeContent = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1px 6px',
        borderRadius: 999,
        border: `1px solid ${border}`,
        background: bg,
        color,
        fontSize: 11,
        fontWeight: 800,
        lineHeight: '16px',
        whiteSpace: 'nowrap',
      }}
    >
      {formatDeltaHours(n)}
      {warningMessage ? (
        <span
          aria-label="Hours overage warning"
          style={{ color: warningColor, marginLeft: 4, fontSize: 12, fontWeight: 900, lineHeight: 1 }}
        >
          !
        </span>
      ) : null}
    </span>
  );

  if (!warningMessage) return badgeContent;

  return (
    <Whisper speaker={<Tooltip>{warningMessage}</Tooltip>} trigger={['hover', 'focus']} placement="top">
      {badgeContent}
    </Whisper>
  );
};

export default function ProjectActualsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const projectId = Number(params?.projectId);
  const taskOrderId = Number(params?.taskOrderId);

  const [printMode, setPrintMode] = useState(false);

  const { data: activeProvider, isLoading: providerLoading } = useActiveProvider();
  const activeProviderName = activeProvider?.name || null;
  const { data: usersRes } = useUsers();
  const { data: closeDatesRes } = useCloseDates();

  const usersList = useMemo(() => {
    const list = Array.isArray(usersRes) ? usersRes : usersRes?.users;
    const arr = Array.isArray(list) ? list : [];
    return arr
      .map((u) => ({
        id: Number(u?.id),
        ftePercent: Number(u?.ftePercent ?? u?.fte_percent ?? 100),
      }))
      .filter((u) => Number.isInteger(u.id));
  }, [usersRes]);

  const userFteById = useMemo(() => {
    const map = new Map();
    for (const u of usersList) {
      map.set(Number(u.id), Number.isFinite(Number(u.ftePercent)) ? Number(u.ftePercent) : 100);
    }
    return map;
  }, [usersList]);

  const closeDates = useMemo(() => {
    const list = Array.isArray(closeDatesRes) ? closeDatesRes : closeDatesRes?.rows;
    return Array.isArray(list) ? list : [];
  }, [closeDatesRes]);

  const WINDOW_SIZE = 6;
  const currentYearStart = useMemo(() => getCurrentYearStartMonthIso(), []);
  const [startMonth, setStartMonth] = useState(() => getCurrentYearStartMonthIso());

  const months = useMemo(() => {
    const list = [];
    for (let i = 0; i < WINDOW_SIZE; i += 1) {
      const ms = addMonthsIso(startMonth, i);
      if (ms) list.push(ms);
    }
    return list;
  }, [WINDOW_SIZE, startMonth]);

  const monthRangeLabel = useMemo(() => {
    if (months.length === 0) return '';
    const first = formatMonthLabel(months[0]);
    const last = formatMonthLabel(months[months.length - 1]);
    return first === last ? first : `${first} – ${last}`;
  }, [months]);

  useEffect(() => {
    if (!printMode) return undefined;

    const handleAfterPrint = () => setPrintMode(false);
    window.addEventListener('afterprint', handleAfterPrint);

    const t = setTimeout(() => {
      try {
        window.print();
      } catch {
        setPrintMode(false);
      }
    }, 60);

    return () => {
      clearTimeout(t);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [printMode]);

  const currentMonthStart = useMemo(() => toMonthStartIso(new Date()), []);
  const isOnCurrentWindow = startMonth === currentYearStart;

  const { data: projects, isLoading: projectLoading } = useProjects();
  const project = useMemo(() => {
    const list = Array.isArray(projects) ? projects : projects?.projects;
    const arr = Array.isArray(list) ? list : [];
    return arr.find((p) => Number(p?.id) === Number(projectId)) || null;
  }, [projectId, projects]);

  const {
    data: taskOrdersData,
    isLoading: taskOrdersLoading,
    error: taskOrdersError,
  } = useTaskOrders(Number.isInteger(projectId) ? projectId : null, { enabled: Number.isInteger(projectId) });

  const taskOrders = useMemo(() => (Array.isArray(taskOrdersData) ? taskOrdersData : []), [taskOrdersData]);
  const selectedTaskOrder = useMemo(() => {
    if (!Number.isInteger(taskOrderId)) return null;
    return taskOrders.find((o) => Number(o?.id) === Number(taskOrderId)) || null;
  }, [taskOrderId, taskOrders]);

  const taskOrderPickerData = useMemo(() => {
    const list = Array.isArray(taskOrders) ? taskOrders : [];

    const isNamed = (o) => {
      const name = typeof o?.name === 'string' ? o.name.trim() : '';
      return name.length > 0;
    };

    // “Explicit task order configured and named” means: not the auto/default row AND has a non-empty name.
    const explicitNamed = list.filter((o) => !o?.isDefault && isNamed(o));

    // Show Default only when there are no explicit named task orders.
    const candidates = explicitNamed.length > 0 ? explicitNamed : list;

    const opts = candidates
      .map((o) => ({ label: o?.name || `Task order ${o?.id}`, value: Number(o?.id), isDefault: !!o?.isDefault }))
      .filter((o) => Number.isInteger(o.value));

    return explicitNamed.length > 0 ? opts.filter((o) => !o.isDefault) : opts;
  }, [taskOrders]);

  const hasMultipleTaskOrders = taskOrderPickerData.length > 1;

  const navigateBack = useCallback(() => {
    const originPath = typeof location?.state?.taskActualsPath === 'string'
      ? location.state.taskActualsPath.trim()
      : '';

    if (location?.state?.fromTaskActuals && originPath) {
      navigate(originPath);
      return;
    }

    if (Number.isInteger(projectId) && Number.isInteger(taskOrderId)) {
      navigate(`/projects/${projectId}/task-orders/${taskOrderId}`);
      return;
    }

    if (Number.isInteger(projectId)) {
      navigate(`/projects/${projectId}`);
      return;
    }

    navigate('/');
  }, [location, navigate, projectId, taskOrderId]);

  useEffect(() => {
    if (!Number.isInteger(projectId)) return;
    if (Number.isInteger(taskOrderId)) return;
    if (!Array.isArray(taskOrders) || taskOrders.length === 0) return;

    // Only auto-pick when there is exactly one option.
    if (taskOrders.length !== 1) return;
    const onlyId = Number(taskOrders[0]?.id);
    if (!Number.isInteger(onlyId)) return;
    navigate(`/projects/${projectId}/task-orders/${onlyId}/actuals`, {
      replace: true,
      state: location.state,
    });
  }, [location.state, navigate, projectId, taskOrderId, taskOrders]);

  const {
    data: forecast,
    isLoading: forecastLoading,
    error: forecastError,
  } = useTaskOrderHourForecastForMonths(
    Number.isInteger(projectId) ? projectId : null,
    Number.isInteger(taskOrderId) ? taskOrderId : null,
    months
  );

  const {
    data: actuals,
    isLoading: actualsLoading,
    error: actualsError,
    refetch: refetchTaskOrderActuals,
  } = useTaskOrderActualsForMonths(
    Number.isInteger(projectId) ? projectId : null,
    Number.isInteger(taskOrderId) ? taskOrderId : null,
    months
  );

  const {
    data: taskMonthRollup,
    isLoading: taskMonthRollupLoading,
    error: taskMonthRollupError,
    refetch: refetchTaskMonthRollup,
  } = useTaskOrderTaskMonthRollup(
    Number.isInteger(projectId) ? projectId : null,
    Number.isInteger(taskOrderId) ? taskOrderId : null,
    months,
    { enabled: months.length > 0 }
  );

  const authenticatedFetch = useAuthenticatedFetch();
  const pullHoursUrlBase = useMemo(() => {
    const apiBase = resolveApiBaseUrl();
    return `${apiBase.replace(/\/+$/, '')}/projects/${encodeURIComponent(String(projectId || ''))}/pull-hours`;
  }, [projectId]);

  const [pullingMonth, setPullingMonth] = useState(null);
  const [refreshAttemptedAtByMonth, setRefreshAttemptedAtByMonth] = useState({});

  const [unmatchedModalOpen, setUnmatchedModalOpen] = useState(false);
  const [unmatchedModalMonth, setUnmatchedModalMonth] = useState(null);
  const [unmatchedModalRows, setUnmatchedModalRows] = useState([]);
  const [unmatchedModalTasks, setUnmatchedModalTasks] = useState([]);

  const [linkStatusModalOpen, setLinkStatusModalOpen] = useState(false);
  const [linkStatusModalMonth, setLinkStatusModalMonth] = useState(null);
  const [linkStatusModalRows, setLinkStatusModalRows] = useState([]);
  const [linkStatusModalLoading, setLinkStatusModalLoading] = useState(false);
  const [linkStatusPendingPullMonth, setLinkStatusPendingPullMonth] = useState(null);
  const [linkStatusIgnoringTaskIds, setLinkStatusIgnoringTaskIds] = useState({});

  const isTimeCampActive = activeProviderName === 'timecamp';

  const pullTimecampHoursForMonth = useCallback(
    async (monthStart) => {
      if (!isTimeCampActive) return;
      if (!Number.isInteger(projectId)) return;
      if (!isIsoDate(monthStart)) return;

      setPullingMonth(monthStart);

      try {
        const resp = await authenticatedFetch(pullHoursUrlBase, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ monthStart, taskOrderId }),
        });

        const json = await resp.json().catch(() => null);
        if (!resp.ok) {
          throw new Error(json?.error || `Failed to pull hours (${resp.status})`);
        }

        const upserted = Number(json?.upserted ?? 0);
        const tasksLinked = Number(json?.tasksLinked ?? 0);
        const tasksPulled = Number(json?.tasksPulled ?? 0);

        setRefreshAttemptedAtByMonth((prev) => ({
          ...prev,
          [monthStart]: normalizeIsoTimestamp(json?.refreshedAt) || new Date().toISOString(),
        }));

        if (tasksLinked <= 0) {
          toaster.push(
            <Message showIcon type="warning" closable>
              No tasks are linked in {activeProviderName || 'the active provider'} for {formatMonthLabel(monthStart)}. Nothing was pulled.
            </Message>,
            { placement: 'topEnd', duration: 6500 }
          );
        } else {
          toaster.push(
            <Message showIcon type="success" closable>
              Pulled TimeCamp hours for {formatMonthLabel(monthStart)}. {tasksPulled}/{tasksLinked} tasks updated ({upserted} rows).
            </Message>,
            { placement: 'topEnd', duration: 3200 }
          );
        }
        const tasksRaw = Array.isArray(json?.unmatchedTasks) ? json.unmatchedTasks : [];
        const unmatchedTasks = tasksRaw
          .map((t) => ({
            taskId: Number(t?.taskId),
            taskName: typeof t?.taskName === 'string' ? t.taskName : null,
            totalHours: Number(t?.totalHours ?? 0),
          }))
          .filter((t) => Number.isInteger(t.taskId) && Number.isFinite(t.totalHours) && t.totalHours > 0)
          .sort((a, b) => String(a.taskName || `Task ${a.taskId}`).localeCompare(String(b.taskName || `Task ${b.taskId}`)));

        const detailsRaw = Array.isArray(json?.unmatchedUsersDetails) ? json.unmatchedUsersDetails : [];
        const details = detailsRaw
          .map((r) => ({
            userName: typeof r?.userName === 'string' ? r.userName.trim() : '',
            hours: Number(r?.hours ?? 0),
          }))
          .filter((r) => r.userName && Number.isFinite(r.hours) && r.hours > 0)
          .sort((a, b) => String(a.userName).localeCompare(String(b.userName)));

        if (unmatchedTasks.length > 0 || details.length > 0) {
          setUnmatchedModalMonth(monthStart);
          setUnmatchedModalRows(details);
          setUnmatchedModalTasks(unmatchedTasks);
          setUnmatchedModalOpen(true);
        } else {
          const unknownUsers = Array.isArray(json?.unmatchedUsers) ? json.unmatchedUsers : [];
          if (unknownUsers.length > 0) {
            const uniq = Array.from(new Set(unknownUsers)).slice(0, 6);
            toaster.push(
              <Message showIcon type="warning" closable>
                Could not match some TimeCamp users: {uniq.join(', ')}{unknownUsers.length > uniq.length ? '…' : ''}
              </Message>,
              { placement: 'topEnd', duration: 6000 }
            );
          }
        }

        if (typeof refetchTaskOrderActuals === 'function') {
          await refetchTaskOrderActuals();
        }
        if (typeof refetchTaskMonthRollup === 'function') {
          await refetchTaskMonthRollup();
        }
      } catch (e) {
        toaster.push(
          <Message showIcon type="error" closable>
            Failed to pull TimeCamp entries for {formatMonthLabel(monthStart)}: {e?.message || String(e)}
          </Message>,
          { placement: 'topEnd', duration: 7000 }
        );
      } finally {
        setPullingMonth(null);
      }
    },
    [
      activeProviderName,
      authenticatedFetch,
      isTimeCampActive,
      projectId,
      pullHoursUrlBase,
      refetchTaskMonthRollup,
      refetchTaskOrderActuals,
      taskOrderId,
    ]
  );

  const fetchProjectTaskLinks = useCallback(async () => {
    const apiBase = resolveApiBaseUrl();
    const url = `${apiBase.replace(/\/+$/, '')}/projects/${encodeURIComponent(String(projectId || ''))}/task-links`;
    const resp = await authenticatedFetch(url);
    const json = await resp.json().catch(() => null);
    if (!resp.ok) {
      throw new Error(json?.error || `Failed to load task links (${resp.status})`);
    }
    return json;
  }, [authenticatedFetch, projectId]);

  const ignoreProjectTaskLinkWarning = useCallback(
    async (taskId) => {
      if (!Number.isInteger(projectId)) {
        throw new Error('Invalid project id');
      }
      if (!Number.isInteger(taskId)) {
        throw new Error('Invalid task id');
      }

      const apiBase = resolveApiBaseUrl();
      const url = `${apiBase.replace(/\/+$/, '')}/projects/${encodeURIComponent(String(projectId || ''))}/task-links/${encodeURIComponent(String(taskId))}/ignore`;
      const resp = await authenticatedFetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isIgnored: true }),
      });
      const json = await resp.json().catch(() => null);
      if (!resp.ok) {
        throw new Error(json?.error || `Failed to ignore task-link warning (${resp.status})`);
      }
      return json;
    },
    [authenticatedFetch, projectId]
  );

  const handlePullClick = useCallback(
    async (monthStart) => {
      if (!isTimeCampActive) return;
      if (!Number.isInteger(projectId)) return;
      if (!isIsoDate(monthStart)) return;
      if (pullingMonth !== null) return;

      setLinkStatusModalLoading(true);
      try {
        const json = await fetchProjectTaskLinks();
        const tasksTotal = Number(json?.tasksTotal ?? 0);
        const tasksLinked = Number(json?.tasksLinked ?? 0);
        const taskLinksRaw = Array.isArray(json?.taskLinks) ? json.taskLinks : [];
        const taskLinks = taskLinksRaw
          .map((r) => ({
            taskId: Number(r?.taskId),
            taskName: typeof r?.taskName === 'string' ? r.taskName : null,
            isLinked: r?.isLinked === true,
            isIgnored: r?.isIgnored === true,
          }))
          .filter((r) => Number.isInteger(r.taskId))
          .sort((a, b) => String(a.taskName || `Task ${a.taskId}`).localeCompare(String(b.taskName || `Task ${b.taskId}`)));

        if (tasksTotal > 0 && tasksLinked === tasksTotal) {
          await pullTimecampHoursForMonth(monthStart);
          return;
        }

        setLinkStatusPendingPullMonth(monthStart);
        setLinkStatusModalMonth(monthStart);
        setLinkStatusModalRows(taskLinks);
        setLinkStatusIgnoringTaskIds({});
        setLinkStatusModalOpen(true);
      } catch (e) {
        toaster.push(
          <Message showIcon type="error" closable>
            Failed to check task linking status: {e?.message || String(e)}
          </Message>,
          { placement: 'topEnd', duration: 7000 }
        );
      } finally {
        setLinkStatusModalLoading(false);
      }
    },
    [fetchProjectTaskLinks, isTimeCampActive, projectId, pullTimecampHoursForMonth, pullingMonth]
  );

  const people = useMemo(() => {
    const byId = new Map();
    const addRows = (rows) => {
      const list = Array.isArray(rows) ? rows : [];
      for (const r of list) {
        const uid = Number(r?.userId);
        if (!Number.isInteger(uid)) continue;
        const name = String(r?.userName || `User ${uid}`);
        if (!byId.has(uid)) byId.set(uid, { userId: uid, name });
      }
    };

    addRows(forecast?.rows);
    addRows(actuals?.rows);

    return Array.from(byId.values()).sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
  }, [actuals?.rows, forecast?.rows]);

  const forecastHoursByUserId = useMemo(() => {
    const map = new Map();
    const rows = Array.isArray(forecast?.rows) ? forecast.rows : [];
    for (const r of rows) {
      const uid = Number(r?.userId);
      if (!Number.isInteger(uid)) continue;
      map.set(uid, r?.hours && typeof r.hours === 'object' ? r.hours : {});
    }
    return map;
  }, [forecast?.rows]);

  const actualHoursByUserId = useMemo(() => {
    const map = new Map();
    const rows = Array.isArray(actuals?.rows) ? actuals.rows : [];
    for (const r of rows) {
      const uid = Number(r?.userId);
      if (!Number.isInteger(uid)) continue;
      map.set(uid, r?.hours && typeof r.hours === 'object' ? r.hours : {});
    }
    return map;
  }, [actuals?.rows]);

  const timecardLastModifiedByUserId = useMemo(() => {
    const map = new Map();
    const rows = Array.isArray(actuals?.rows) ? actuals.rows : [];
    for (const r of rows) {
      const uid = Number(r?.userId);
      if (!Number.isInteger(uid)) continue;
      const ts = normalizeIsoTimestamp(r?.timecardLastModifiedAt);
      if (!ts) continue;
      const prev = map.get(uid);
      if (!prev || ts > prev) map.set(uid, ts);
    }
    return map;
  }, [actuals?.rows]);

  const monthLastModifiedByMonth = useMemo(() => {
    const map = {};
    const raw = actuals?.monthLastModifiedByMonth;
    if (!raw || typeof raw !== 'object') return map;

    for (const [monthStartRaw, tsRaw] of Object.entries(raw)) {
      const monthStart = isIsoDate(monthStartRaw) ? monthStartRaw : null;
      const ts = normalizeIsoTimestamp(tsRaw);
      if (!monthStart || !ts) continue;
      const prev = map[monthStart];
      if (!prev || ts > prev) map[monthStart] = ts;
    }

    return map;
  }, [actuals?.monthLastModifiedByMonth]);

  const taskBreakdownReconciliationByMonth = useMemo(() => {
    const out = {};
    for (const m of months) {
      const topProj = Number(forecast?.totals?.[m] ?? 0);
      const topAct = Number(actuals?.totals?.[m] ?? 0);
      const byTaskProj = Number(taskMonthRollup?.totals?.projected?.[m] ?? 0);
      const byTaskAct = Number(taskMonthRollup?.totals?.actual?.[m] ?? 0);
      out[m] = {
        projected: topProj - byTaskProj,
        actual: topAct - byTaskAct,
      };
    }
    return out;
  }, [actuals?.totals, forecast?.totals, months, taskMonthRollup?.totals?.actual, taskMonthRollup?.totals?.projected]);

  const hasTaskBreakdownReconciliationRow = useMemo(
    () =>
      months.some((m) => {
        const diff = taskBreakdownReconciliationByMonth?.[m];
        return Math.abs(Number(diff?.projected ?? 0)) >= 0.01 || Math.abs(Number(diff?.actual ?? 0)) >= 0.01;
      }),
    [months, taskBreakdownReconciliationByMonth]
  );

  const taskBreakdownReconciliationDetails = useMemo(() => {
    return months
      .map((m) => {
        const topProjected = Number(forecast?.totals?.[m] ?? 0);
        const topActual = Number(actuals?.totals?.[m] ?? 0);
        const byTaskProjected = Number(taskMonthRollup?.totals?.projected?.[m] ?? 0);
        const byTaskActual = Number(taskMonthRollup?.totals?.actual?.[m] ?? 0);
        const gapProjected = topProjected - byTaskProjected;
        const gapActual = topActual - byTaskActual;
        return {
          month: m,
          topProjected,
          byTaskProjected,
          gapProjected,
          topActual,
          byTaskActual,
          gapActual,
          hasGap: Math.abs(gapProjected) >= 0.01 || Math.abs(gapActual) >= 0.01,
        };
      })
      .filter((r) => r.hasGap);
  }, [actuals?.totals, forecast?.totals, months, taskMonthRollup?.totals?.actual, taskMonthRollup?.totals?.projected]);

  const colTemplate = useMemo(() => {
    const monthCols = months.map(() => 'minmax(150px, 1fr)').join(' ');
    return `minmax(110px, 1fr) ${monthCols}`.trim();
  }, [months]);

  const goPrevMonths = () => {
    const ns = addMonthsIso(startMonth, -WINDOW_SIZE);
    if (ns) setStartMonth(ns);
  };

  const goNextMonths = () => {
    const ns = addMonthsIso(startMonth, WINDOW_SIZE);
    if (ns) setStartMonth(ns);
  };

  const goCurrentMonths = () => {
    setStartMonth(currentYearStart);
  };

  const handleExportXlsx = useCallback(async () => {
    const labelProject = project?.name || (Number.isInteger(projectId) ? `Project ${projectId}` : 'Project');
    const labelTaskOrder = selectedTaskOrder?.name || (Number.isInteger(taskOrderId) ? `Task order ${taskOrderId}` : '');
    const baseName = `Actuals - ${labelProject}${labelTaskOrder ? ` - ${labelTaskOrder}` : ''} - ${monthRangeLabel || 'Export'}`;
    const fileName = `${baseName.replace(/[\\/:*?"<>|]+/g, '-')}.xlsx`;

    const rollupColumns = [
      { header: 'Person', key: 'person', width: 32 },
      ...months.flatMap((m) => [
        { header: `${formatMonthLabel(m)} Proj`, key: `p:${m}`, width: 14, numFmt: '0.00' },
        { header: `${formatMonthLabel(m)} Act`, key: `a:${m}`, width: 14, numFmt: '0.00' },
      ]),
    ];

    const isFuture = (m) => String(m).slice(0, 7) > String(currentMonthStart).slice(0, 7);

    const rollupRows = people.map((p) => {
      const uid = Number(p?.userId);
      const out = { person: String(p?.name || `User ${uid}`) };
      for (const m of months) {
        const proj = Number(forecastHoursByUserId.get(uid)?.[m] ?? 0);
        const act = isFuture(m) ? '' : Number(actualHoursByUserId.get(uid)?.[m] ?? 0);
        out[`p:${m}`] = proj;
        out[`a:${m}`] = act;
      }
      return out;
    });

    const totalRow = { person: 'Total' };
    for (const m of months) {
      const proj = Number(forecast?.totals?.[m] ?? 0);
      const act = isFuture(m) ? '' : Number(actuals?.totals?.[m] ?? 0);
      totalRow[`p:${m}`] = proj;
      totalRow[`a:${m}`] = act;
    }
    rollupRows.push(totalRow);

    const taskBreakdownColumns = [
      { header: 'Task', key: 'task', width: 42 },
      ...months.flatMap((m) => [
        { header: `${formatMonthLabel(m)} Proj`, key: `p:${m}`, width: 14, numFmt: '0.00' },
        { header: `${formatMonthLabel(m)} Act`, key: `a:${m}`, width: 14, numFmt: '0.00' },
      ]),
    ];

    const breakdownSource = Array.isArray(taskMonthRollup?.rows) ? taskMonthRollup.rows : [];
    const taskBreakdownRows = breakdownSource
      .map((t) => {
        const taskId = Number(t?.taskId);
        const taskName = typeof t?.taskName === 'string' ? t.taskName : null;
        const projected = t?.projected && typeof t.projected === 'object' ? t.projected : {};
        const actual = t?.actual && typeof t.actual === 'object' ? t.actual : {};
        const out = { task: taskName || (Number.isInteger(taskId) ? `Task ${taskId}` : 'Task') };
        for (const m of months) {
          out[`p:${m}`] = Number(projected?.[m] ?? 0);
          out[`a:${m}`] = isFuture(m) ? '' : Number(actual?.[m] ?? 0);
        }
        return out;
      })
      .sort((a, b) => String(a.task).localeCompare(String(b.task)));

    if (hasTaskBreakdownReconciliationRow) {
      const reconciliationRow = { task: 'Unattributed (needs remap)' };
      for (const m of months) {
        const diff = taskBreakdownReconciliationByMonth?.[m] || {};
        reconciliationRow[`p:${m}`] = Number(diff?.projected ?? 0);
        reconciliationRow[`a:${m}`] = isFuture(m) ? '' : Number(diff?.actual ?? 0);
      }
      taskBreakdownRows.push(reconciliationRow);
    }

    if (taskBreakdownRows.length > 0) {
      const breakdownTotals = { task: 'Total' };
      for (const m of months) {
        const sp = Number(forecast?.totals?.[m] ?? 0);
        const sa = Number(actuals?.totals?.[m] ?? 0);
        breakdownTotals[`p:${m}`] = sp;
        breakdownTotals[`a:${m}`] = isFuture(m) ? '' : sa;
      }
      taskBreakdownRows.push(breakdownTotals);
    }

    await exportSheetsToXlsx({
      fileName,
      sheets: [
        { name: 'Rollup', columns: rollupColumns, rows: rollupRows },
        { name: 'Task breakdown', columns: taskBreakdownColumns, rows: taskBreakdownRows },
      ],
    });
  }, [
    actualHoursByUserId,
    actuals?.totals,
    currentMonthStart,
    forecast?.totals,
    forecastHoursByUserId,
    monthRangeLabel,
    months,
    people,
    project?.name,
    projectId,
    selectedTaskOrder?.name,
    hasTaskBreakdownReconciliationRow,
    taskBreakdownReconciliationByMonth,
    taskMonthRollup?.rows,
    taskOrderId,
  ]);

  if (!Number.isInteger(taskOrderId)) {
    return (
      <div className="ppc-page">
        {taskOrdersError ? (
          <Message type="error" showIcon>
            Failed to load task orders.
          </Message>
        ) : null}

        <Panel bordered>
          <Stack spacing={12} alignItems="center" wrap>
            <div style={{ minWidth: 240 }}>
              <div style={{ fontWeight: 800, marginBottom: 6 }}>Select a task order</div>
              <SelectPicker
                block
                placeholder={taskOrdersLoading ? 'Loading task orders…' : 'Choose a task order'}
                disabled={taskOrdersLoading || taskOrderPickerData.length === 0}
                data={taskOrderPickerData}
                value={null}
                onChange={(value) => {
                  const id = Number(value);
                  if (!Number.isInteger(projectId) || !Number.isInteger(id)) return;
                  navigate(`/projects/${projectId}/task-orders/${id}/actuals`, { state: location.state });
                }}
              />
              {taskOrders.length === 0 && !taskOrdersLoading ? (
                <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>No task orders found for this project.</div>
              ) : null}
            </div>

            <Button
              appearance="ghost"
              size="sm"
              onClick={navigateBack}
            >
              Back
            </Button>
          </Stack>
        </Panel>
      </div>
    );
  }

  return (
    <>
    <div className="ppc-noprint">
      {Number.isInteger(taskOrderId) && !selectedTaskOrder && taskOrders.length > 0 ? (
        <div style={{ marginBottom: 12 }}>
          <Message type="error" showIcon>
            Task order not found in this project.
          </Message>
        </div>
      ) : null}

      <Panel
        bordered
        header={
          <Stack justifyContent="space-between" alignItems="center" wrap spacing={10}>
            <Stack spacing={10} alignItems="center" wrap>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <h3 style={{ margin: 0, lineHeight: '22px' }}>Actuals</h3>
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>
                  {projectLoading
                    ? 'Loading project…'
                    : project?.name
                      ? project.name
                      : Number.isInteger(projectId)
                        ? `Project ${projectId}`
                        : '—'}
                  {Number.isInteger(taskOrderId)
                    ? ` • ${selectedTaskOrder?.name || `Task order ${taskOrderId}`}`
                    : ''}
                </div>
              </div>
            </Stack>

            <Stack spacing={10} wrap>
              {hasMultipleTaskOrders ? (
                <div style={{ minWidth: 240 }}>
                  <SelectPicker
                    block
                    searchable={false}
                    cleanable={false}
                    placeholder={taskOrdersLoading ? 'Loading task orders…' : 'Task order'}
                    disabled={taskOrdersLoading || taskOrderPickerData.length === 0}
                    data={taskOrderPickerData}
                    value={Number.isInteger(taskOrderId) ? taskOrderId : null}
                    onChange={(value) => {
                      const id = Number(value);
                      if (!Number.isInteger(projectId) || !Number.isInteger(id)) return;
                      navigate(`/projects/${projectId}/task-orders/${id}/actuals`, { state: location.state });
                    }}
                  />
                </div>
              ) : null}
              <Button
                appearance="ghost"
                size="sm"
                onClick={navigateBack}
              >
                Back
              </Button>
            </Stack>
          </Stack>
        }
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <ProviderBadge provider={activeProvider} isLoading={providerLoading} />
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
            <IconButton
              appearance="subtle"
              size="sm"
              icon={<i className="bi bi-chevron-left" aria-hidden="true" />}
              aria-label="Previous months"
              title="Previous months"
              onClick={goPrevMonths}
            />
            <div style={{ color: '#666', fontSize: 12, fontWeight: 700, textAlign: 'right', whiteSpace: 'nowrap' }}>{monthRangeLabel}</div>
            <IconButton
              appearance="subtle"
              size="sm"
              icon={<i className="bi bi-chevron-right" aria-hidden="true" />}
              aria-label="Next months"
              title="Next months"
              onClick={goNextMonths}
            />
            <Button appearance="subtle" size="sm" onClick={goCurrentMonths} disabled={isOnCurrentWindow}>
              Current
            </Button>

            <Button
              appearance="subtle"
              size="sm"
              onClick={handleExportXlsx}
              disabled={forecastLoading || actualsLoading || taskMonthRollupLoading}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <FileExcelO aria-hidden="true" />
                Export
              </span>
            </Button>

            <Button appearance="subtle" size="sm" onClick={() => setPrintMode(true)} disabled={printMode}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <i className="bi bi-printer" aria-hidden="true" />
                Print
              </span>
            </Button>
          </div>
        </div>

        {!providerLoading && !activeProviderName ? (
          <div style={{ marginBottom: 10 }}>
            <Message type="warning" showIcon closable>
              You have not configured the source for your actual hours.{' '}
              <Button appearance="link" size="xs" onClick={() => navigate('/configuration')}>
                Click here to configure
              </Button>
            </Message>
          </div>
        ) : null}

        <div style={{ overflowX: 'auto', paddingBottom: 6 }}>
          <div
            style={{
              minWidth: 820,
              display: 'grid',
              gridTemplateColumns: colTemplate,
              gap: 0,
              alignItems: 'stretch',
              border: '1px solid #f0f0f0',
              borderRadius: 10,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                fontWeight: 900,
                padding: '10px 10px',
                borderBottom: '1px solid #f0f0f0',
                background: '#fafafa',
              }}
            >
              Person
            </div>
            {months.map((m) => (
              <div
                key={m}
                style={{
                  padding: '10px 10px',
                  borderBottom: '1px solid #f0f0f0',
                  textAlign: 'center',
                  fontVariantNumeric: 'tabular-nums',
                  background: '#fafafa',
                }}
              >
                {(() => {
                  const isFutureMonth = String(m).slice(0, 7) > String(currentMonthStart).slice(0, 7);
                  const hasStoredFromApi = Array.isArray(actuals?.monthsWithRows)
                    ? actuals.monthsWithRows.includes(m)
                    : false;
                  const localRefreshAt = refreshAttemptedAtByMonth?.[m] || null;
                  const hasStored = hasStoredFromApi || Boolean(monthLastModifiedByMonth?.[m]) || Boolean(localRefreshAt);
                  const refreshedAt = latestIsoTimestamp(monthLastModifiedByMonth?.[m], localRefreshAt);
                  const refreshedDaysLabel = formatRefreshedLabel(refreshedAt);
                  const canPull =
                    isTimeCampActive &&
                    Number.isInteger(projectId) &&
                    !forecastLoading &&
                    !actualsLoading &&
                    !isFutureMonth;

                  return (
                    <>
                      <div style={{ fontWeight: 900 }}>{formatMonthLabel(m)}</div>
                      {canPull ? (
                        <div style={{ marginTop: 6 }}>
                          <Whisper
                            placement="top"
                            speaker={<Tooltip>Pull TimeCamp hours for all linked tasks for this month</Tooltip>}
                            trigger={['hover', 'focus']}
                          >
                            <Button
                              appearance="ghost"
                              size="xs"
                              loading={pullingMonth === m}
                              disabled={pullingMonth !== null}
                              onClick={() => handlePullClick(m)}
                            >
                              {hasStored ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                  <i className="bi bi-arrow-repeat" aria-hidden="true" />
                                  Refresh
                                </span>
                              ) : (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                  <i className="bi bi-arrow-down" aria-hidden="true" />
                                  Pull
                                </span>
                              )}
                            </Button>
                          </Whisper>
                          {refreshedDaysLabel ? (
                            <div style={{ marginTop: 4, fontSize: 11, color: '#888', lineHeight: '14px' }}>
                              Refreshed {refreshedDaysLabel}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </>
                  );
                })()}
              </div>
            ))}

            {people.length === 0 ? (
              <div style={{ gridColumn: `1 / span ${months.length + 1}`, padding: 12 }}>
                <Message type="info" showIcon>
                  No forecast/actual hours found for this project in this range.
                </Message>
              </div>
            ) : null}

            {people.map((p) => (
              <div key={p.userId} style={{ display: 'contents' }}>
                {(() => {
                  const timecardUpdatedLabel = formatDaysAgoLabel(timecardLastModifiedByUserId.get(Number(p.userId)));
                  return (
                <div
                  style={{
                    padding: '10px 10px',
                    borderBottom: '1px solid #f0f0f0',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={p.name}
                >
                  <div style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                  {timecardUpdatedLabel ? (
                    <div style={{ fontSize: 11, color: '#888', lineHeight: '14px' }}>
                      Timecard updated {timecardUpdatedLabel}
                    </div>
                  ) : null}
                </div>
                  );
                })()}

                {months.map((m) => (
                  <div
                    key={`${p.userId}|${m}`}
                    style={{
                      padding: '10px 10px',
                      borderBottom: '1px solid #f0f0f0',
                      textAlign: 'center',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {(() => {
                      const proj = Number(forecastHoursByUserId.get(p.userId)?.[m] ?? 0);
                      const act = Number(actualHoursByUserId.get(p.userId)?.[m] ?? 0);
                      const showDelta = !forecastLoading && !actualsLoading;
                      const delta = showDelta ? act - proj : null;
                      const isFutureMonth = String(m).slice(0, 7) > String(currentMonthStart).slice(0, 7);
                      const shortfallWarning =
                        showDelta &&
                        !isFutureMonth &&
                        shouldWarnProjectedShortfall({
                          monthStart: m,
                          projected: proj,
                          actual: act,
                          currentMonthStart,
                          ftePercent: userFteById.get(Number(p.userId)) ?? 100,
                          closeDates,
                        });

                      return (
                        <div>
                          <div
                            style={{
                              fontSize: 12,
                              color: '#666',
                              display: 'flex',
                              alignItems: 'baseline',
                              justifyContent: 'center',
                              gap: 12,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <span>
                              <span style={{ fontWeight: 800, marginRight: 4 }}>Proj</span>
                              {forecastLoading ? '…' : formatHours(proj)}
                            </span>
                            {!isFutureMonth ? (
                              <span>
                                <span style={{ fontWeight: 800, marginRight: 4 }}>Act</span>
                                {actualsLoading ? '…' : formatHours(act)}
                              </span>
                            ) : null}
                          </div>
                          <div style={{ marginTop: 6, height: 1, background: '#e6e6e6' }} />
                          <div
                            style={{
                              marginTop: 6,
                              height: 18,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            {showDelta && !isFutureMonth ? (
                              <DeltaBadge delta={delta} projected={proj} actual={act} shortfallWarning={shortfallWarning} />
                            ) : null}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            ))}

            <div style={{ display: 'contents' }}>
              <div
                style={{
                  padding: '10px 10px',
                  borderTop: '2px solid #e6e6e6',
                  fontWeight: 900,
                  background: '#fcfcfc',
                }}
              >
                Total
              </div>

              {months.map((m) => (
                <div
                  key={`totals|${m}`}
                  style={{
                    padding: '10px 10px',
                    borderTop: '2px solid #e6e6e6',
                    textAlign: 'center',
                    fontVariantNumeric: 'tabular-nums',
                    background: '#fcfcfc',
                  }}
                >
                  {(() => {
                    const proj = Number(forecast?.totals?.[m] ?? 0);
                    const act = Number(actuals?.totals?.[m] ?? 0);
                    const showDelta = !forecastLoading && !actualsLoading;
                    const delta = showDelta ? act - proj : null;
                    const isFutureMonth = String(m).slice(0, 7) > String(currentMonthStart).slice(0, 7);

                    return (
                      <div>
                        <div
                          style={{
                            fontSize: 12,
                            color: '#444',
                            display: 'flex',
                            alignItems: 'baseline',
                            justifyContent: 'center',
                            gap: 12,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <span>
                            <span style={{ fontWeight: 800, marginRight: 4 }}>Proj</span>
                            {forecastLoading ? '…' : formatHours(proj)}
                          </span>
                          {!isFutureMonth ? (
                            <span>
                              <span style={{ fontWeight: 800, marginRight: 4 }}>Act</span>
                              {actualsLoading ? '…' : formatHours(act)}
                            </span>
                          ) : null}
                        </div>
                        <div style={{ marginTop: 6, height: 1, background: '#e6e6e6' }} />
                        <div
                          style={{
                            marginTop: 6,
                            height: 18,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          {showDelta && !isFutureMonth ? <DeltaBadge delta={delta} projected={proj} actual={act} /> : null}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          </div>

          {forecastError ? (
            <div style={{ marginTop: 10 }}>
              <Message type="error" showIcon>
                Failed to load projected hours.
              </Message>
            </div>
          ) : null}

          {actualsError ? (
            <div style={{ marginTop: 10 }}>
              <Message type="error" showIcon>
                Failed to load actual hours.
              </Message>
            </div>
          ) : null}
        </div>

        <div style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ fontWeight: 900 }}>Task breakdown</div>
            </div>

            {taskMonthRollupError ? (
              <div style={{ marginTop: 10 }}>
                <Message type="error" showIcon>
                  Failed to load task breakdown.
                </Message>
              </div>
            ) : null}

            {taskMonthRollupLoading ? (
              <div style={{ marginTop: 10 }}>
                <Message type="info" showIcon>
                  Loading task breakdown…
                </Message>
              </div>
            ) : null}

            {!taskMonthRollupLoading && !taskMonthRollupError ? (
              (() => {
                const rows = Array.isArray(taskMonthRollup?.rows) ? taskMonthRollup.rows : [];

                if (rows.length === 0) {
                  return (
                    <div style={{ marginTop: 10 }}>
                      <Message type="info" showIcon>
                        No task-level hours found in this range.
                      </Message>
                    </div>
                  );
                }

                const totalProjectedByMonth = forecast?.totals && typeof forecast.totals === 'object'
                  ? forecast.totals
                  : {};
                const totalActualByMonth = actuals?.totals && typeof actuals.totals === 'object'
                  ? actuals.totals
                  : {};

                return (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ overflowX: 'auto', paddingBottom: 6 }}>
                      <div
                        style={{
                          minWidth: 820,
                          display: 'grid',
                          gridTemplateColumns: colTemplate,
                          gap: 0,
                          alignItems: 'stretch',
                          border: '1px solid #f0f0f0',
                          borderRadius: 10,
                          overflow: 'hidden',
                        }}
                      >
                        <div style={{ fontWeight: 900, padding: '10px 10px', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>Task</div>
                        {months.map((m) => (
                          <div
                            key={`taskBreakdownHeader|${m}`}
                            style={{
                              padding: '10px 10px',
                              borderBottom: '1px solid #f0f0f0',
                              textAlign: 'center',
                              fontVariantNumeric: 'tabular-nums',
                              background: '#fafafa',
                            }}
                          >
                            <div style={{ fontWeight: 900 }}>{formatMonthLabel(m)}</div>
                          </div>
                        ))}

                        {rows.map((t) => {
                          const taskId = Number(t?.taskId);
                          const taskName = typeof t?.taskName === 'string' ? t.taskName : null;
                          const projected = t?.projected && typeof t.projected === 'object' ? t.projected : {};
                          const actual = t?.actual && typeof t.actual === 'object' ? t.actual : {};

                          return (
                            <div key={taskId} style={{ display: 'contents' }}>
                              <div
                                style={{
                                  padding: '10px 10px',
                                  borderBottom: '1px solid #f5f5f5',
                                  minWidth: 0,
                                  whiteSpace: 'normal',
                                  overflowWrap: 'anywhere',
                                }}
                                title={taskName || `Task ${taskId}`}
                              >
                                <Button
                                  appearance="link"
                                  size="xs"
                                  style={{
                                    padding: 0,
                                    fontWeight: 800,
                                    maxWidth: '100%',
                                    textAlign: 'left',
                                    whiteSpace: 'normal',
                                    overflowWrap: 'anywhere',
                                    lineHeight: '1.3',
                                  }}
                                  onClick={() => {
                                    if (!Number.isInteger(taskId)) return;
                                    if (Number.isInteger(projectId) && Number.isInteger(taskOrderId)) {
                                      navigate(`/projects/${projectId}/task-orders/${taskOrderId}/tasks/${taskId}/actuals`);
                                      return;
                                    }
                                    navigate(`/tasks/${taskId}/actuals`);
                                  }}
                                >
                                  {taskName || `Task ${taskId}`}
                                </Button>
                              </div>

                              {months.map((m) => {
                                const proj = Number(projected?.[m] ?? 0);
                                const act = Number(actual?.[m] ?? 0);
                                const showDelta = !taskMonthRollupLoading;
                                const delta = showDelta ? act - proj : null;
                                const isFutureMonth = String(m).slice(0, 7) > String(currentMonthStart).slice(0, 7);

                                return (
                                  <div
                                    key={`${taskId}|${m}`}
                                    style={{
                                      padding: '10px 10px',
                                      borderBottom: '1px solid #f5f5f5',
                                      textAlign: 'center',
                                      fontVariantNumeric: 'tabular-nums',
                                    }}
                                  >
                                    <div>
                                      <div
                                        style={{
                                          fontSize: 12,
                                          color: '#666',
                                          display: 'flex',
                                          alignItems: 'baseline',
                                          justifyContent: 'center',
                                          gap: 12,
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        <span>
                                          <span style={{ fontWeight: 800, marginRight: 4 }}>Proj</span>
                                          {taskMonthRollupLoading ? '…' : formatHours(proj)}
                                        </span>
                                        {!isFutureMonth ? (
                                          <span>
                                            <span style={{ fontWeight: 800, marginRight: 4 }}>Act</span>
                                            {taskMonthRollupLoading ? '…' : formatHours(act)}
                                          </span>
                                        ) : null}
                                      </div>
                                      <div style={{ marginTop: 6, height: 1, background: '#e6e6e6' }} />
                                      <div
                                        style={{
                                          marginTop: 6,
                                          height: 18,
                                          display: 'flex',
                                          justifyContent: 'center',
                                          alignItems: 'center',
                                        }}
                                      >
                                        {showDelta && !isFutureMonth ? <DeltaBadge delta={delta} projected={proj} actual={act} /> : null}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}

                        {hasTaskBreakdownReconciliationRow ? (
                          <div style={{ display: 'contents' }}>
                            <div
                              style={{
                                padding: '10px 10px',
                                borderBottom: '1px solid #f5f5f5',
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontStyle: 'italic',
                                color: '#666',
                              }}
                              title="Hours that are included in user totals but are not attributed to a task row in this breakdown."
                            >
                              Unattributed (needs remap)
                            </div>

                            {months.map((m) => {
                              const rec = taskBreakdownReconciliationByMonth?.[m] || {};
                              const proj = Number(rec?.projected ?? 0);
                              const act = Number(rec?.actual ?? 0);
                              const showDelta = !taskMonthRollupLoading;
                              const delta = showDelta ? act - proj : null;
                              const isFutureMonth = String(m).slice(0, 7) > String(currentMonthStart).slice(0, 7);

                              return (
                                <div
                                  key={`taskBreakdownRecon|${m}`}
                                  style={{
                                    padding: '10px 10px',
                                    borderBottom: '1px solid #f5f5f5',
                                    textAlign: 'center',
                                    fontVariantNumeric: 'tabular-nums',
                                    background: '#fcfcfc',
                                  }}
                                >
                                  <div>
                                    <div
                                      style={{
                                        fontSize: 12,
                                        color: '#666',
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        justifyContent: 'center',
                                        gap: 12,
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      <span>
                                        <span style={{ fontWeight: 800, marginRight: 4 }}>Proj</span>
                                        {formatHours(proj)}
                                      </span>
                                      {!isFutureMonth ? (
                                        <span>
                                          <span style={{ fontWeight: 800, marginRight: 4 }}>Act</span>
                                          {formatHours(act)}
                                        </span>
                                      ) : null}
                                    </div>
                                    <div style={{ marginTop: 6, height: 1, background: '#e6e6e6' }} />
                                    <div
                                      style={{
                                        marginTop: 6,
                                        height: 18,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                      }}
                                    >
                                      {showDelta && !isFutureMonth ? <DeltaBadge delta={delta} projected={proj} actual={act} /> : null}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : null}

                        {hasTaskBreakdownReconciliationRow ? (
                          <div style={{ gridColumn: `1 / span ${months.length + 1}`, padding: '10px 12px', background: '#fffaf2', borderBottom: '1px solid #f5f5f5' }}>
                            <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>
                              Reconciliation details
                            </div>
                            <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                              This row appears when top-line totals and task-level totals do not match for one or more months. Commonly this is temporary right after a pull; refresh keeps it in sync.
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                  <tr>
                                    <th style={{ textAlign: 'left', padding: '4px 6px' }}>Month</th>
                                    <th style={{ textAlign: 'right', padding: '4px 6px' }}>Top Proj</th>
                                    <th style={{ textAlign: 'right', padding: '4px 6px' }}>By-task Proj</th>
                                    <th style={{ textAlign: 'right', padding: '4px 6px' }}>Gap Proj</th>
                                    <th style={{ textAlign: 'right', padding: '4px 6px' }}>Top Act</th>
                                    <th style={{ textAlign: 'right', padding: '4px 6px' }}>By-task Act</th>
                                    <th style={{ textAlign: 'right', padding: '4px 6px' }}>Gap Act</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {taskBreakdownReconciliationDetails.map((r) => (
                                    <tr key={`tb-recon-detail|${r.month}`}>
                                      <td style={{ padding: '4px 6px' }}>{formatMonthLabel(r.month)}</td>
                                      <td style={{ textAlign: 'right', padding: '4px 6px' }}>{formatHours(r.topProjected)}</td>
                                      <td style={{ textAlign: 'right', padding: '4px 6px' }}>{formatHours(r.byTaskProjected)}</td>
                                      <td style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 800 }}>{formatHours(r.gapProjected)}</td>
                                      <td style={{ textAlign: 'right', padding: '4px 6px' }}>{formatHours(r.topActual)}</td>
                                      <td style={{ textAlign: 'right', padding: '4px 6px' }}>{formatHours(r.byTaskActual)}</td>
                                      <td style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 800 }}>{formatHours(r.gapActual)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : null}

                        <div style={{ display: 'contents' }}>
                          <div
                            style={{
                              padding: '10px 10px',
                              borderBottom: '1px solid #f0f0f0',
                              background: '#fafafa',
                              fontWeight: 900,
                            }}
                          >
                            Total
                          </div>

                          {months.map((m) => {
                            const proj = Number(totalProjectedByMonth?.[m] ?? 0);
                            const act = Number(totalActualByMonth?.[m] ?? 0);
                            const delta = act - proj;
                            const isFutureMonth = String(m).slice(0, 7) > String(currentMonthStart).slice(0, 7);

                            return (
                              <div
                                key={`taskBreakdownTotal|${m}`}
                                style={{
                                  padding: '10px 10px',
                                  borderBottom: '1px solid #f0f0f0',
                                  textAlign: 'center',
                                  fontVariantNumeric: 'tabular-nums',
                                  background: '#fafafa',
                                }}
                              >
                                <div>
                                  <div
                                    style={{
                                      fontSize: 12,
                                      color: '#666',
                                      display: 'flex',
                                      alignItems: 'baseline',
                                      justifyContent: 'center',
                                      gap: 12,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    <span>
                                      <span style={{ fontWeight: 800, marginRight: 4 }}>Proj</span>
                                      {formatHours(proj)}
                                    </span>
                                    {!isFutureMonth ? (
                                      <span>
                                        <span style={{ fontWeight: 800, marginRight: 4 }}>Act</span>
                                        {formatHours(act)}
                                      </span>
                                    ) : null}
                                  </div>
                                  <div style={{ marginTop: 6, height: 1, background: '#e6e6e6' }} />
                                  <div
                                    style={{
                                      marginTop: 6,
                                      height: 18,
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                    }}
                                  >
                                    {!isFutureMonth ? <DeltaBadge delta={delta} projected={proj} actual={act} /> : null}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()
            ) : null}
        </div>

        <Modal open={unmatchedModalOpen} onClose={() => setUnmatchedModalOpen(false)} size="sm">
          <Modal.Header>
            <Modal.Title>Unmatched TimeCamp users</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Message type="warning" showIcon>
              Some TimeCamp users could not be matched to your app users for {formatMonthLabel(unmatchedModalMonth)}. Their hours were not saved.
              {unmatchedModalTasks.length > 0 ? (
                <div style={{ marginTop: 6 }}>
                  Open the affected task below to remediate (Accept / Add to task / Kick back) and pull again.
                </div>
              ) : null}
            </Message>

            <div style={{ marginTop: 12 }}>
              {unmatchedModalTasks.length > 0 ? (
                <div style={{ marginBottom: unmatchedModalRows.length > 0 ? 14 : 0 }}>
                  <div style={{ fontSize: 12, color: '#666', fontWeight: 800, marginBottom: 8 }}>Tasks affected</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center' }}>
                    {unmatchedModalTasks.map((t) => {
                      const label = t.taskName || `Task ${t.taskId}`;
                      const month = isIsoDate(unmatchedModalMonth) ? unmatchedModalMonth : null;

                      return (
                        <div key={t.taskId} style={{ display: 'contents' }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={label}>
                            <Button
                              appearance="link"
                              size="xs"
                              onClick={() => {
                                setUnmatchedModalOpen(false);
                                if (!Number.isInteger(projectId) || !Number.isInteger(taskOrderId)) return;
                                const qs = month ? `?pullMonthStart=${encodeURIComponent(month)}` : '';
                                navigate(`/projects/${projectId}/task-orders/${taskOrderId}/tasks/${t.taskId}/actuals${qs}`);
                              }}
                              style={{ padding: 0, fontWeight: 800 }}
                            >
                              {label}
                            </Button>
                          </div>
                          <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 800 }}>
                            {formatHours(t.totalHours)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {unmatchedModalRows.length === 0 ? (
                <div style={{ color: '#666', fontSize: 12 }}>No details available.</div>
              ) : (
                <div>
                  <div style={{ fontSize: 12, color: '#666', fontWeight: 800, marginBottom: 8 }}>
                    Unmatched users (rollup summary)
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                  {unmatchedModalRows.map((r) => (
                    <div key={r.userName} style={{ display: 'contents' }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.userName}>
                        {r.userName}
                      </div>
                      <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 800 }}>
                        {formatHours(r.hours)}
                      </div>
                    </div>
                  ))}
                </div>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button appearance="primary" onClick={() => setUnmatchedModalOpen(false)}>
              OK
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal open={linkStatusModalOpen} onClose={() => setLinkStatusModalOpen(false)} size="md">
          <Modal.Header>
            <Modal.Title>Task linking status</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Message type="warning" showIcon>
              Not all tasks are linked in {activeProviderName || 'the active provider'} for {formatMonthLabel(linkStatusModalMonth)}.
            </Message>

            <div style={{ marginTop: 12 }}>
              {linkStatusModalRows.length === 0 ? (
                <div style={{ color: '#666', fontSize: 12 }}>No tasks found for this project.</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 200px', gap: 8, alignItems: 'center' }}>
                  <div style={{ fontSize: 12, color: '#666', fontWeight: 800 }}>Task</div>
                  <div style={{ fontSize: 12, color: '#666', fontWeight: 800, textAlign: 'center' }}>Status</div>
                  <div />

                  {linkStatusModalRows.map((r) => {
                    const name = r.taskName || `Task ${r.taskId}`;
                    const isIgnoring = linkStatusIgnoringTaskIds[r.taskId] === true;
                    const isIgnored = r.isIgnored === true;
                    const statusLabel = r.isLinked ? 'Linked' : isIgnored ? 'Ignored' : 'Missing';
                    const statusColor = r.isLinked ? '#389e0d' : isIgnored ? '#0958d9' : '#cf1322';
                    const statusBg = r.isLinked ? '#f6ffed' : isIgnored ? '#e6f4ff' : '#fff1f0';
                    const statusBorder = r.isLinked ? '#b7eb8f' : isIgnored ? '#91caff' : '#ffa39e';

                    return (
                      <div key={r.taskId} style={{ display: 'contents' }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={name}>
                          {name}
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '1px 8px',
                              borderRadius: 999,
                              border: `1px solid ${statusBorder}`,
                              background: statusBg,
                              color: statusColor,
                              fontSize: 11,
                              fontWeight: 900,
                              lineHeight: '18px',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {statusLabel}
                          </span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          {!r.isLinked && !isIgnored ? (
                            <Stack spacing={6} justifyContent="flex-end">
                              <Button
                                appearance="subtle"
                                size="xs"
                                loading={isIgnoring}
                                disabled={isIgnoring || linkStatusModalLoading}
                                onClick={async () => {
                                  setLinkStatusIgnoringTaskIds((prev) => ({ ...prev, [r.taskId]: true }));
                                  try {
                                    await ignoreProjectTaskLinkWarning(r.taskId);
                                    setLinkStatusModalRows((prev) =>
                                      prev.map((row) =>
                                        row.taskId === r.taskId ? { ...row, isIgnored: true } : row
                                      )
                                    );
                                    toaster.push(
                                      <Message showIcon type="success" closable>
                                        Ignored warning for {name}.
                                      </Message>,
                                      { placement: 'topEnd', duration: 3500 }
                                    );
                                  } catch (e) {
                                    toaster.push(
                                      <Message showIcon type="error" closable>
                                        Failed to ignore warning: {e?.message || String(e)}
                                      </Message>,
                                      { placement: 'topEnd', duration: 7000 }
                                    );
                                  } finally {
                                    setLinkStatusIgnoringTaskIds((prev) => {
                                      const next = { ...prev };
                                      delete next[r.taskId];
                                      return next;
                                    });
                                  }
                                }}
                              >
                                Ignore
                              </Button>
                              <Button
                                appearance="ghost"
                                size="xs"
                                onClick={() => {
                                  setLinkStatusModalOpen(false);
                                  navigate(`/tasks/${r.taskId}/actuals`);
                                }}
                              >
                                Link task
                              </Button>
                            </Stack>
                          ) : (
                            <Button
                              appearance="subtle"
                              size="xs"
                              onClick={() => {
                                setLinkStatusModalOpen(false);
                                navigate(`/tasks/${r.taskId}/actuals`);
                              }}
                            >
                              Open
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button appearance="subtle" onClick={() => setLinkStatusModalOpen(false)} disabled={linkStatusModalLoading}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              loading={linkStatusModalLoading || pullingMonth === linkStatusPendingPullMonth}
              disabled={!linkStatusPendingPullMonth || pullingMonth !== null}
              onClick={async () => {
                const m = linkStatusPendingPullMonth;
                setLinkStatusModalOpen(false);
                if (!m) return;
                await pullTimecampHoursForMonth(m);
              }}
            >
              Pull anyway
            </Button>
          </Modal.Footer>
        </Modal>
      </Panel>
    </div>

    <div className="ppc-print-area" aria-hidden={!printMode}>
      <div className="ppc-print-header">
        <div className="ppc-print-header-row">
          <div className="ppc-print-title">Actuals (Rollup)</div>
          <div className="ppc-print-meta">
            <div className="ppc-print-meta-item">
              Project: <span style={{ fontWeight: 700 }}>{project?.name || (Number.isInteger(projectId) ? `Project ${projectId}` : '—')}</span>
            </div>
            <div className="ppc-print-meta-item">
              Task order: <span style={{ fontWeight: 700 }}>{selectedTaskOrder?.name || (Number.isInteger(taskOrderId) ? `Task order ${taskOrderId}` : '—')}</span>
            </div>
            <div className="ppc-print-meta-item">
              Range: <span style={{ fontWeight: 700 }}>{monthRangeLabel}</span>
            </div>
          </div>
        </div>
      </div>

      <table className="ppc-print-table">
        <thead>
          <tr>
            <th rowSpan={2} style={{ textAlign: 'left' }}>Person</th>
            {months.map((m) => (
              <th key={`mh:${m}`} colSpan={2}>{formatMonthLabel(m)}</th>
            ))}
          </tr>
          <tr>
            {months.flatMap((m) => [
              <th key={`subp:${m}`}>Proj</th>,
              <th key={`suba:${m}`}>Act</th>,
            ])}
          </tr>
        </thead>
        <tbody>
          {people.length === 0 ? (
            <tr>
              <td colSpan={1 + months.length * 2} style={{ padding: 8, color: '#666' }}>
                No forecast/actual hours found for this range.
              </td>
            </tr>
          ) : (
            people.map((p) => {
              const uid = Number(p?.userId);
              const hasUser = Number.isInteger(uid);

              return (
                <tr key={`p:${uid}`}>
                  <td style={{ minWidth: 220 }}>{p?.name || `User ${uid}`}</td>
                  {months.flatMap((m) => {
                    const proj = hasUser ? Number(forecastHoursByUserId.get(uid)?.[m] ?? 0) : 0;
                    const isFutureMonth = String(m).slice(0, 7) > String(currentMonthStart).slice(0, 7);
                    const act = isFutureMonth ? '' : (hasUser ? Number(actualHoursByUserId.get(uid)?.[m] ?? 0) : 0);
                    return [
                      <td key={`pp:${uid}|${m}`} className="ppc-num">{Number(proj).toFixed(2)}</td>,
                      <td key={`pa:${uid}|${m}`} className="ppc-num">{act === '' ? '' : Number(act).toFixed(2)}</td>,
                    ];
                  })}
                </tr>
              );
            })
          )}

          <tr className="ppc-print-total-row">
            <td>Total</td>
            {months.flatMap((m) => {
              const proj = Number(forecast?.totals?.[m] ?? 0);
              const isFutureMonth = String(m).slice(0, 7) > String(currentMonthStart).slice(0, 7);
              const act = isFutureMonth ? '' : Number(actuals?.totals?.[m] ?? 0);
              return [
                <td key={`tp:${m}`} className="ppc-num">{Number(proj).toFixed(2)}</td>,
                <td key={`ta:${m}`} className="ppc-num">{act === '' ? '' : Number(act).toFixed(2)}</td>,
              ];
            })}
          </tr>
        </tbody>
      </table>

      {(() => {
        const rows = Array.isArray(taskMonthRollup?.rows) ? taskMonthRollup.rows : [];
        const pageBreak = rows.length > 0;

        return (
          <div className={`ppc-print-section${pageBreak ? ' ppc-print-page-break' : ''}`}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Task breakdown</div>

            <table className="ppc-print-table">
              <thead>
                <tr>
                  <th rowSpan={2} style={{ textAlign: 'left' }}>Task</th>
                  {months.map((m) => (
                    <th key={`tbh:${m}`} colSpan={2}>{formatMonthLabel(m)}</th>
                  ))}
                </tr>
                <tr>
                  {months.flatMap((m) => [
                    <th key={`tbsubp:${m}`}>Proj</th>,
                    <th key={`tbsuba:${m}`}>Act</th>,
                  ])}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={1 + months.length * 2} style={{ padding: 8, color: '#666' }}>
                      No task-level hours found for this range.
                    </td>
                  </tr>
                ) : (
                  rows.map((t) => {
                    const taskId = Number(t?.taskId);
                    const taskName = typeof t?.taskName === 'string' ? t.taskName : null;
                    const projected = t?.projected && typeof t.projected === 'object' ? t.projected : {};
                    const actual = t?.actual && typeof t.actual === 'object' ? t.actual : {};

                    return (
                      <tr key={`tb:${Number.isInteger(taskId) ? taskId : taskName || 'task'}`}>
                        <td style={{ minWidth: 260 }}>{taskName || (Number.isInteger(taskId) ? `Task ${taskId}` : 'Task')}</td>
                        {months.flatMap((m) => {
                          const proj = Number(projected?.[m] ?? 0);
                          const isFutureMonth = String(m).slice(0, 7) > String(currentMonthStart).slice(0, 7);
                          const act = isFutureMonth ? '' : Number(actual?.[m] ?? 0);
                          return [
                            <td key={`tbp:${taskId}|${m}`} className="ppc-num">{Number(proj).toFixed(2)}</td>,
                            <td key={`tba:${taskId}|${m}`} className="ppc-num">{act === '' ? '' : Number(act).toFixed(2)}</td>,
                          ];
                        })}
                      </tr>
                    );
                  })
                )}
                {rows.length > 0 ? (
                  <>
                  {hasTaskBreakdownReconciliationRow ? (
                    <tr>
                      <td style={{ minWidth: 260, fontStyle: 'italic', color: '#666' }}>Unattributed (needs remap)</td>
                      {months.flatMap((m) => {
                        const rec = taskBreakdownReconciliationByMonth?.[m] || {};
                        const proj = Number(rec?.projected ?? 0);
                        const isFutureMonth = String(m).slice(0, 7) > String(currentMonthStart).slice(0, 7);
                        const act = isFutureMonth ? '' : Number(rec?.actual ?? 0);
                        return [
                          <td key={`tbpr:${m}`} className="ppc-num">{Number(proj).toFixed(2)}</td>,
                          <td key={`tbar:${m}`} className="ppc-num">{act === '' ? '' : Number(act).toFixed(2)}</td>,
                        ];
                      })}
                    </tr>
                  ) : null}
                  <tr>
                    <td style={{ minWidth: 260, fontWeight: 900, background: '#fafafa' }}>Total</td>
                    {months.flatMap((m) => {
                      const proj = Number(forecast?.totals?.[m] ?? 0);
                      const isFutureMonth = String(m).slice(0, 7) > String(currentMonthStart).slice(0, 7);
                      const act = isFutureMonth ? '' : Number(actuals?.totals?.[m] ?? 0);
                      return [
                        <td key={`tbpt:${m}`} className="ppc-num" style={{ background: '#fafafa', fontWeight: 900 }}>{Number(proj).toFixed(2)}</td>,
                        <td key={`tbat:${m}`} className="ppc-num" style={{ background: '#fafafa', fontWeight: 900 }}>{act === '' ? '' : Number(act).toFixed(2)}</td>,
                      ];
                    })}
                  </tr>
                  </>
                ) : null}
              </tbody>
            </table>
          </div>
        );
      })()}
    </div>
    </>
  );
}
